import { type PropsWithChildren, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { FetchPage } from "@/lib/fetch-helper";
import type { IEntryPointComponent, IMasterResult } from "@/src/common/PageTransition";
import { usePageContext } from "@/src/contexts/PageContext";
import { useUserAuthenticationContext } from "@/src/contexts/UserAuthenticationContext";
import { useQueryClientCustom, useQueryCustom } from "@/src/query-client-overrides";
import {
    type DefaultError,
    QueryClient,
    QueryClientProvider,
    type QueryKey,
    type UseSuspenseQueryOptions,
} from "@tanstack/react-query";

function getQueryClient() {
    return new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
                retry: false,
                refetchOnMount: false,
                staleTime: 1000 * 60,
            },
        },
    });
}

let initialData: IMasterResult<IEntryPointComponent>;

export const GetQueryClientCustom = <TOutput extends IEntryPointComponent>(data?: IMasterResult<TOutput>) => {
    initialData = data;

    return getQueryClient();
};

export const getInitialData = <TOutput extends IEntryPointComponent>() => {
    if (!initialData) return undefined;

    const data = initialData as IMasterResult<TOutput>;
    initialData = undefined;

    return {
        body: data,
        status: 200,
    } as PageResult<TOutput>;
};

type QueryPageOptions<TOutput extends IEntryPointComponent> = Partial<
    UseSuspenseQueryOptions<PageResult<TOutput>, DefaultError, PageResult<TOutput>, QueryKey>
>;

type PageResult<TOutput extends IEntryPointComponent> = {
    body: IMasterResult<TOutput>;
    status: number;
};

export function useQueryPageCustom<TOutput extends IEntryPointComponent>({
    ignoreQuery,
    endpoint,
    ...options
}: Omit<QueryPageOptions<TOutput>, "queryFn"> & {
    ignoreQuery?: boolean;
    endpoint?: string;
} = {}) {
    const { pathname, search } = useLocation();
    const { isAuthenticated, userId } = useUserAuthenticationContext();

    endpoint = endpoint ?? (ignoreQuery ? pathname : pathname + search);
    const queryKey = [pathname, !isAuthenticated || userId];

    const result = useQueryCustom<PageResult<TOutput>>({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey,
        queryFn: async () => {
            const response = await FetchPage<IMasterResult<TOutput>>(endpoint, { Entry: endpoint });
            if (response.status === 401) {
                window.location.href = "/#zaloguj";
                return null;
            }

            return response;
        },
        initialData: getInitialData<TOutput>(),
        refetchOnMount: true,
        ...options,
    });

    const { setPageContext } = usePageContext();

    useEffect(() => {
        if (result.data?.body) {
            setPageContext(result.data.body);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result.data]);

    return { ...result, data: result.data?.body };
}

export function useInvalidateQueryPageCustom() {
    const { pathname, search } = useLocation();
    const { userId } = useUserAuthenticationContext();
    const queryClient = useQueryClientCustom();

    const endpoint = pathname + search;

    useEffect(
        () => () => {
            queryClient.removeQueries({
                queryKey: [endpoint, userId],
            });
        },
        [queryClient, endpoint, userId],
    );
}

export function QueryProviderCustom({
    children,
    queryClient,
}: PropsWithChildren<{ queryClient: QueryClient }>) {
    return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
